import React, {
  forwardRef, memo, useCallback, useEffect, useImperativeHandle,
  useMemo, useRef, useState,
} from "react";
import { useLocalization } from "@fluent/react";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  Alert, Box, Button, CircularProgress, Divider, IconButton, MenuItem,
  Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers";
import { useEditor } from "@tiptap/react";
import AddTaskIcon from "@mui/icons-material/AddTask";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import SubjectOutlinedIcon from "@mui/icons-material/SubjectOutlined";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";

import { useAppDispatch } from "../../../app/hooks";
import { RichTextContainer, mkTipTapExtWithMention } from "../../../app/RichTextEditor";
import { showError } from "../../notifications/notificationsSlice";
import { updateMeetingMinuteItem } from "../../project/mettingMinutes/meetingMinutesSlice";
import type { MeetingMinuteItem } from "../../project/mettingMinutes/meetingMinutesSlice";
import type { Attendee } from "../../project/mettingMinutes/meeting";
import type { ProjectProcessHeader } from "../../project/projectProcess/ProjectProcessApiClient";

type EditableFields = Pick<MeetingMinuteItem,
  "name" | "description" | "responsiblePerson" | "deadline" |
  "priority" | "status" | "projectProcessId"
>;

function editableFields(item: MeetingMinuteItem): EditableFields {
  return {
    name: item.name,
    description: item.description,
    responsiblePerson: item.responsiblePerson,
    deadline: item.deadline,
    priority: item.priority,
    status: item.status,
    projectProcessId: item.projectProcessId,
  };
}

/** Nie zastępuj całego wiersza starym snapshotem: tasks/history mogły się zmienić. */
export function mergeMeetingItemDetails(
  current: MeetingMinuteItem,
  saved: MeetingMinuteItem,
): MeetingMinuteItem {
  return { ...current, ...editableFields(saved) };
}

export interface NewMeetingItemDetailsHandle {
  flush: () => Promise<boolean>;
  hasUnsavedChanges: () => boolean;
}

export interface NewMeetingItemDetailsProps {
  item?: MeetingMinuteItem | null;
  isEditable: boolean;
  attendees: Attendee[];
  processes: ProjectProcessHeader[];
  onSaved: (saved: MeetingMinuteItem) => void;
  onAddTask?: (item: MeetingMinuteItem) => void;
  onDelete?: (item: MeetingMinuteItem) => void;
  onClose?: () => void;
  onSaveError?: (error: unknown, item: MeetingMinuteItem) => void;
  debounceMs?: number;
}

type SaveStatus = "saved" | "pending" | "saving" | "error";
type ItemSession = {
  key: string;
  source: MeetingMinuteItem;
  draft: EditableFields;
  revision: number;
  savedRevision: number;
  status: SaveStatus;
  timer: ReturnType<typeof setTimeout> | null;
  inFlight: Promise<boolean> | null;
};

// Osobna kolejka dla każdej instancji panelu. Brak globalnego stanu.
// Wszystkie requesty dla danego tematu wykonują się kolejno.
function createItemSaveQueue(callbacks: {
  save: (item: MeetingMinuteItem) => Promise<void>;
  saved: (item: MeetingMinuteItem) => void;
  error: (error: unknown, item: MeetingMinuteItem) => void;
}) {
  const sessions = new Map<string, ItemSession>();
  let listener: (() => void) | undefined;
  const notify = () => listener?.();
  const clearTimer = (session: ItemSession) => {
    if (session.timer !== null) clearTimeout(session.timer);
    session.timer = null;
  };

  const get = (item: MeetingMinuteItem) => {
    const key = `${item.itemType}:${item.id}`;
    let session = sessions.get(key);
    if (!session) {
      session = {
        key, source: item, draft: editableFields(item), revision: 0,
        savedRevision: 0, status: "saved", timer: null, inFlight: null,
      };
      sessions.set(key, session);
    } else {
      // Odświeżamy pola nieedytowane, ale nie nadpisujemy lokalnego szkicu
      // odpowiedzią wcześniejszego zapisu ani nieaktualnym selectedRow rodzica.
      session.source = item;
    }
    return session;
  };

  const snapshot = (session: ItemSession): MeetingMinuteItem => ({
    ...session.source, ...session.draft,
  });

  const flush = (session: ItemSession): Promise<boolean> => {
    clearTimer(session);
    if (session.inFlight) return session.inFlight;
    if (session.savedRevision === session.revision) return Promise.resolve(true);

    session.status = "saving";
    notify();
    session.inFlight = (async () => {
      while (session.savedRevision < session.revision) {
        clearTimer(session);
        const sentRevision = session.revision;
        const sent = snapshot(session);
        try {
          await callbacks.save(sent);
        } catch (error) {
          session.status = "error";
          // Po błędzie zostawiamy najnowszy szkic. Ponowienie jest jawne.
          clearTimer(session);
          callbacks.error(error, sent);
          return false;
        }
        session.savedRevision = sentRevision;
        callbacks.saved(sent);
        // Jeżeli podczas requestu dopisano tekst, następny request otrzyma
        // najnowszy kompletny szkic. Nie uruchamiamy go równolegle.
      }
      session.status = "saved";
      return true;
    })().finally(() => {
      session.inFlight = null;
      notify();
    });
    return session.inFlight;
  };

  const update = (session: ItemSession, patch: Partial<EditableFields>, delay: number) => {
    if (!(Object.keys(patch) as Array<keyof EditableFields>)
      .some(key => !Object.is(session.draft[key], patch[key]))) return;
    session.draft = { ...session.draft, ...patch };
    session.revision += 1;
    clearTimer(session);
    if (session.status !== "saving" && session.status !== "pending") {
      session.status = "pending";
      notify();
    }
    session.timer = setTimeout(() => { void flush(session); }, delay);
  };

  return {
    get, snapshot, update, flush,
    subscribe(callback: () => void) {
      listener = callback;
      return () => { listener = undefined; };
    },
    flushAll: async () => {
      const results = await Promise.all(Array.from(sessions.values(), flush));
      return results.every(Boolean);
    },
    hasUnsavedChanges: () => Array.from(sessions.values())
      .some(session => session.revision !== session.savedRevision),
    failed: () => Array.from(sessions.values()).filter(session => session.status === "error"),
  };
}

type SaveQueue = ReturnType<typeof createItemSaveQueue>;

export const NewMeetingItemDetails = memo(forwardRef<
  NewMeetingItemDetailsHandle, NewMeetingItemDetailsProps
>(function NewMeetingItemDetails(props, ref) {
  const dispatch = useAppDispatch();
  const { l10n } = useLocalization();
  const tr = (key: string, fallback: string) => l10n.getString(key, null, `_${fallback}`);
  const callbacks = useRef({ dispatch, onSaved: props.onSaved, onSaveError: props.onSaveError });
  useEffect(() => {
    callbacks.current = { dispatch, onSaved: props.onSaved, onSaveError: props.onSaveError };
  }, [dispatch, props.onSaved, props.onSaveError]);

  const [queue] = useState(() => createItemSaveQueue({
    save: async item => {
      await callbacks.current.dispatch(updateMeetingMinuteItem(item)).then(unwrapResult);
    },
    saved: item => callbacks.current.onSaved(item),
    error: (error, item) => {
      if (callbacks.current.onSaveError) callbacks.current.onSaveError(error, item);
      else callbacks.current.dispatch(showError("error"));
    },
  }));
  const [, redrawStatus] = useState(0);

  useEffect(() => {
    const unsubscribe = queue.subscribe(() => redrawStatus(value => value + 1));
    return () => {
      unsubscribe();
      // Dodatkowe zabezpieczenie. Przy kontrolowanym wyjściu użyj ref.flush().
      void queue.flushAll();
    };
  }, [queue]);

  useImperativeHandle(ref, () => ({
    flush: queue.flushAll,
    hasUnsavedChanges: queue.hasUnsavedChanges,
  }), [queue]);

  const session = props.item ? queue.get(props.item) : undefined;
  useEffect(() => {
    // Przejście A -> B przyspiesza zapis A. Nie czyści jego szkicu.
    return () => { if (session) void queue.flush(session); };
  }, [queue, session]);

  const failures = queue.failed();
  return <Stack spacing={1.5} sx={{ minWidth: 0 }}>
    {failures.length > 0 && <Alert severity="error" action={
      <Button color="inherit" size="small" onClick={() => { void queue.flushAll(); }}>
        {tr("meeting-details-retry", "Ponów zapis")}
      </Button>
    }>
      {tr("meeting-details-save-error", "Nie udało się zapisać zmian. Szkic pozostaje w panelu.")}
      {" "}{failures.map(entry => entry.draft.name).join(", ")}
    </Alert>}
    {session && props.item ? <ItemDetailsForm
      key={session.key}
      {...props}
      item={props.item}
      session={session}
      queue={queue}
    /> : <Paper variant="outlined" sx={{ p: 4, borderRadius: 2.5, textAlign: "center" }}>
      <SubjectOutlinedIcon color="action" sx={{ fontSize: 32, mb: 1 }} />
      <Typography variant="body2" color="text.secondary">
        {tr("meeting-details-select", "Wybierz temat lub zadanie z listy.")}
      </Typography>
    </Paper>}
  </Stack>;
}));

type FormProps = NewMeetingItemDetailsProps & {
  item: MeetingMinuteItem;
  session: ItemSession;
  queue: SaveQueue;
};

function ItemDetailsForm({
  item, session, queue, isEditable, attendees, processes,
  onAddTask, onDelete, onClose, debounceMs = 600,
}: FormProps) {
  const { l10n } = useLocalization();
  const tr = (key: string, fallback: string) => l10n.getString(key, null, `_${fallback}`);
  const theme = useTheme();
  const soft = alpha(theme.palette.primary.main, 0.09);
  const [fields, setFields] = useState<EditableFields>(() => ({ ...session.draft }));
  const [deadline, setDeadline] = useState<Date | null>(() =>
    session.draft.deadline ? new Date(session.draft.deadline) : null);
  const [deadlineInvalid, setDeadlineInvalid] = useState(false);
  const [runningAction, setRunningAction] = useState(false);

  const change = useCallback((patch: Partial<EditableFields>) => {
    if (!isEditable) return;
    queue.update(session, patch, debounceMs);
    setFields({ ...session.draft });
  }, [isEditable, queue, session, debounceMs]);

  const changeDescription = useCallback((description: string) => {
    if (!isEditable) return;
    queue.update(session, { description }, debounceMs);
    // Nie wywołujemy setFields: treść przechowuje Tiptap.
    // Następna zmiana dowolnego pola korzysta z najnowszego session.draft.
  }, [isEditable, queue, session, debounceMs]);

  const mentions = useMemo(() => attendees.map(person => ({ id: person.email, label: person.person })), [attendees]);
  const mentionsKey = JSON.stringify(mentions);

  const runAction = async (action?: (current: MeetingMinuteItem) => void) => {
    if (!action || runningAction || deadlineInvalid) return;
    setRunningAction(true);
    try {
      if (await queue.flush(session)) action(queue.snapshot(session));
    } finally {
      setRunningAction(false);
    }
  };

  const statusLabel = {
    saved: tr("meeting-details-saved", "Zapisano"),
    pending: tr("meeting-details-pending", "Oczekuje na zapis…"),
    saving: tr("meeting-details-saving", "Zapisywanie…"),
    error: tr("meeting-details-error", "Błąd zapisu"),
  }[session.status];

  return <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2.5, minWidth: 0,
    "& .MuiButton-root": { textTransform: "none", borderRadius: 2 },
    "& .MuiOutlinedInput-root": { borderRadius: 2 },
  }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ width: 34, height: 34, flexShrink: 0, borderRadius: 1.5, bgcolor: soft, color: "primary.main", display: "grid", placeItems: "center" }}>
          {item.itemType === "TASK" ? <TaskOutlinedIcon fontSize="small" /> : <SubjectOutlinedIcon fontSize="small" />}
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {item.itemType === "TASK" ? tr("meeting-minutes-task-details", "Szczegóły zadania") : tr("meeting-details-topic", "Szczegóły tematu")}
        </Typography>
      </Stack>
      {onClose && <IconButton size="small" disabled={runningAction || deadlineInvalid} aria-label={tr("close", "Zamknij")} onClick={() => { void runAction(() => onClose()); }}>
        <CloseIcon fontSize="small" />
      </IconButton>}
    </Stack>

    <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} alignItems="center" sx={{ mb: 2 }}>
      {onAddTask && <Button size="small" variant="outlined" disabled={!isEditable || runningAction || deadlineInvalid} startIcon={<AddTaskIcon />} onClick={() => { void runAction(onAddTask); }}>
        {tr("meeting-minutes-task", "Zadanie")}
      </Button>}
      {onDelete && <Button size="small" color="error" disabled={!isEditable || runningAction || deadlineInvalid} startIcon={<DeleteOutlineIcon />} onClick={() => { void runAction(onDelete); }}>
        {tr("delete", "Usuń")}
      </Button>}
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ ml: "auto" }} aria-live="polite">
        {session.status === "saving" && <CircularProgress size={13} />}
        <Typography variant="caption" color={session.status === "error" ? "error.main" : "text.secondary"}>
          {session.revision === 0 ? tr("meeting-details-autosave", "Zapis automatyczny") : statusLabel}
        </Typography>
      </Stack>
    </Stack>

    <Stack spacing={2}>
      <TextField fullWidth size="small" disabled={!isEditable || runningAction} label={tr("meeting-minutes-topic-issue", "Temat / problem")}
        value={fields.name ?? ""} onChange={event => change({ name: event.target.value })} />
      {item.itemType === "ITEM" && <TextField select fullWidth size="small" disabled={!isEditable || runningAction} label={tr("process", "Proces")}
        value={fields.projectProcessId == null ? "" : String(fields.projectProcessId)}
        onChange={event => change({ projectProcessId: event.target.value === "" ? null : Number(event.target.value) })}>
        <MenuItem value="">{tr("noprocess", "Bez procesu")}</MenuItem>
        {processes.map(process => <MenuItem key={process.id} value={String(process.id)}>{process.name}</MenuItem>)}
      </TextField>}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
        <TextField fullWidth size="small" disabled={!isEditable || runningAction} label={tr("responsible", "Odpowiedzialny")}
          value={fields.responsiblePerson ?? ""} onChange={event => change({ responsiblePerson: event.target.value })} />
        <DatePicker label={tr("deadline", "Termin")} value={deadline} disabled={!isEditable || runningAction} views={["year", "month", "day"]}
          onChange={value => {
            setDeadline(value);
            const invalid = value !== null && Number.isNaN(value.getTime());
            setDeadlineInvalid(invalid);
            if (!invalid) change({ deadline: value ? value.toISOString() : null });
          }}
          slotProps={{ textField: { fullWidth: true, size: "small", error: deadlineInvalid,
            helperText: deadlineInvalid ? tr("field-invalid-date", "Data jest nieprawidłowa") : undefined } }} />
        <TextField select fullWidth size="small" disabled={!isEditable || runningAction} label={tr("priority", "Priorytet")}
          value={fields.priority} onChange={event => change({ priority: event.target.value as EditableFields["priority"] })}>
          <MenuItem value="L"><FlagOutlinedIcon color="disabled" fontSize="small" sx={{ mr: 1 }} />{tr("meeting-minutes-prior-low", "Niski")}</MenuItem>
          <MenuItem value="M"><FlagOutlinedIcon color="info" fontSize="small" sx={{ mr: 1 }} />{tr("meeting-minutes-prior-medium", "Normalny")}</MenuItem>
          <MenuItem value="H"><FlagOutlinedIcon color="error" fontSize="small" sx={{ mr: 1 }} />{tr("meeting-minutes-prior-high", "Wysoki")}</MenuItem>
        </TextField>
        <TextField select fullWidth size="small" disabled={!isEditable || runningAction} label={tr("status", "Status")}
          value={fields.status} onChange={event => change({ status: event.target.value as EditableFields["status"] })}>
          <MenuItem value="NEW">{tr("meeting-minutes-status-new", "Nowe")}</MenuItem>
          <MenuItem value="ACTIVE">{tr("meeting-minutes-status-active", "W trakcie")}</MenuItem>
          <MenuItem value="FINISHED">{tr("meeting-minutes-status-finished", "Zakończone")}</MenuItem>
          <MenuItem value="CANCELED">{tr("meeting-minutes-status-canceled", "Anulowane")}</MenuItem>
        </TextField>
      </Box>
      <DescriptionEditor key={mentionsKey} initialContent={session.draft.description ?? ""} mentions={mentions}
        editable={isEditable && !runningAction} onChange={changeDescription} label={tr("meeting-minutes-task-current-status", "Opis aktualnego stanu")} />
    </Stack>

    <Divider sx={{ my: 2.5 }} />
    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>{tr("meeting-minutes-task-history", "Historia przebiegu")}</Typography>
    {!item.history?.length ? <Typography variant="body2" color="text.secondary">{tr("meeting-details-no-history", "Brak wcześniejszych wpisów.")}</Typography>
      : <TableContainer sx={{ maxHeight: 320 }}>
        <Table stickyHeader size="small">
          <TableHead><TableRow><TableCell sx={{ width: 135 }}>{tr("date", "Data")}</TableCell><TableCell>{tr("entry", "Wpis")}</TableCell></TableRow></TableHead>
          <TableBody>{item.history.map(entry => <TableRow key={entry.id}>
            <TableCell sx={{ verticalAlign: "top" }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {entry.createDate ? new Date(entry.createDate).toLocaleDateString() : "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary">{entry.createdByFullName}</Typography>
            </TableCell>
            <TableCell sx={{ overflowWrap: "anywhere", "& p": { mt: 0 } }}>
              {/* Tak jak w dotychczasowym ekranie: HTML historii z aplikacji. */}
              <div dangerouslySetInnerHTML={{ __html: entry.description }} />
            </TableCell>
          </TableRow>)}</TableBody>
        </Table>
      </TableContainer>}
  </Paper>;
}

// Tiptap ma własny komponent: transakcje edytora nie renderują całego formularza.
// Nie wyłączamy shouldRerenderOnTransaction, aby nie zmienić zachowania toolbaru
// w istniejącym RichTextContainer i zachować zgodność z używaną wersją Tiptap.
const DescriptionEditor = memo(function DescriptionEditor({
  initialContent, mentions, editable, onChange, label,
}: {
  initialContent: string;
  mentions: Array<{ id: string; label: string }>;
  editable: boolean;
  onChange: (html: string) => void;
  label: string;
}) {
  const [contentAtMount] = useState(initialContent);
  const extensions = useMemo(() => mkTipTapExtWithMention(mentions), [mentions]);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  const editor = useEditor({
    extensions,
    content: contentAtMount || "<p></p>",
    editable,
    onUpdate: ({ editor: current }) => onChangeRef.current(current.getHTML()),
  });
  useEffect(() => {
    if (editor) editor.setOptions({ editable });
  }, [editor, editable]);

  return <Box sx={{ "& .tiptap": { minHeight: 150 } }}>
    <RichTextContainer label={<span>{label}</span>} editor={editor} />
  </Box>;
});
